import { createFileRoute, redirect } from '@tanstack/react-router'
import { EmployeeForm, type EmployeeFormSection } from '@/features/hr-employees/employee-form'
import { hasGrant } from '@/lib/me'

export const EDIT_SECTIONS: readonly EmployeeFormSection[] = [
  'identity',
  'employment',
  'personal',
  'financial',
]

export type EditSearch = { section?: EmployeeFormSection }

/**
 * Route shell for editing an employee. The form renders each gated field group
 * only when the active HR grant permits editing it, while the API remains the
 * authority for both the employee scope and the individual group permissions.
 *
 * The employee grant is still required because both sub-resource controllers
 * also enforce the employee's data scope before accepting a write.
 *
 * `?section=` deep-links from the detail screen's readiness warnings to the
 * group that needs attention — the form scrolls to it and highlights it. Any
 * other value is dropped, and a section the caller may not edit simply does
 * not render, so the param is a hint, never a capability.
 */
export const Route = createFileRoute('/_authenticated/hr/employees/$employeeId/edit')({
  beforeLoad: ({ context, params }) => {
    if (!hasGrant(context.queryClient, 'employee.edit')) {
      throw redirect({ to: '/hr/employees/$employeeId', params: { employeeId: params.employeeId } })
    }
  },
  validateSearch: (search: Record<string, unknown>): EditSearch => ({
    section: EDIT_SECTIONS.includes(search.section as EmployeeFormSection)
      ? (search.section as EmployeeFormSection)
      : undefined,
  }),
  component: EmployeeEditRoute,
})

function EmployeeEditRoute() {
  const { employeeId } = Route.useParams()
  const { section } = Route.useSearch()

  return <EmployeeForm employeeId={employeeId} section={section} />
}
